import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getCourses } from '../../services/lmsService';
import { Card, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import Footer from '../Footer';
import { 
  Search, BookOpen, Clock, Users, Filter, 
  GraduationCap, PlayCircle, Video, ChevronRight,
  Sparkles, RefreshCw
} from 'lucide-react';
import { isMobileApp } from '../../utils/platform';

// Utility function to strip HTML tags from text
const stripHtmlTags = (html) => {
  if (!html) return '';
  // Create a temporary div to parse HTML and extract text
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const CourseCatalog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, openAuth } = useContext(AuthContext);
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_items: 0 });
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [mode, setMode] = useState(searchParams.get('mode') || '');
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || '');
  const [selectedCategories, setSelectedCategories] = useState([]);  // NEW
  const [categories, setCategories] = useState([]);  // NEW
  
  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lms/categories`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  const fetchCourses = async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: 12 };
      if (search) params.search = search;
      if (mode) params.mode = mode;
      if (difficulty) params.difficulty_level = difficulty;
      if (selectedCategories.length > 0) params.categories = selectedCategories.join(',');  // NEW
      
      const response = await getCourses(params);
      if (response.success) {
        setCourses(response.data.courses);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCourses();
  }, [search, mode, difficulty, selectedCategories]);  // Added selectedCategories
  
  const toggleCategory = (categoryName) => {
    setSelectedCategories(prev =>
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    const newSearch = e.target.search.value;
    setSearch(newSearch);
    setSearchParams(prev => {
      if (newSearch) prev.set('search', newSearch);
      else prev.delete('search');
      return prev;
    });
  };
  
  const handleFilterChange = (key, value) => {
    if (key === 'mode') setMode(value === 'all' ? '' : value);
    if (key === 'difficulty') setDifficulty(value === 'all' ? '' : value);
    setSearchParams(prev => {
      if (value && value !== 'all') prev.set(key, value);
      else prev.delete(key);
      return prev;
    });
  };
  
  const getModeIcon = (courseMode) => {
    switch (courseMode) {
      case 'live_web': return <Video className="w-4 h-4" />;
      case 'self_paced': return <PlayCircle className="w-4 h-4" />;
      case 'hybrid': return <Sparkles className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };
  
  const getModeLabel = (courseMode) => {
    switch (courseMode) {
      case 'live_web': return 'Live Web';
      case 'self_paced': return 'Self-Paced';
      case 'hybrid': return 'Hybrid';
      default: return courseMode;
    }
  };
  
  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  const formatPrice = (price, discountPrice) => {
    if (price === 0) return <span className="text-lg font-bold text-green-600">Free</span>;
    if (discountPrice) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-indigo-600">₹{discountPrice.toLocaleString()}</span>
          <span className="text-sm text-gray-400 line-through">₹{price.toLocaleString()}</span>
        </div>
      );
    }
    return <span className="text-lg font-bold text-indigo-600">₹{price.toLocaleString()}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50">
      {/* Header Section */}
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <GraduationCap className="h-8 w-8 text-indigo-600" />
                  Smart Class Courses
                </h1>
                <p className="text-gray-600 text-base sm:text-lg">
                  Learn from industry experts with live interactive classes
                </p>
              </div>
              {user && (
                <Button 
                  onClick={() => navigate('/my-learning')}
                  className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                >
                  My Learning
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  name="search"
                  placeholder="Search courses by title or topic..."
                  defaultValue={search}
                  className="pl-10 pr-24 py-3 text-base"
                />
                <Button type="submit" size="sm" className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700">
                  Search
                </Button>
              </div>
            </form>
            
            {/* Category Filter - NEW */}
            {categories.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="text-indigo-600">●</span>
                  Categories
                </h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <Badge
                      key={category.name}
                      onClick={() => toggleCategory(category.name)}
                      className={`cursor-pointer transition-all px-4 py-2 ${
                        selectedCategories.includes(category.name)
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {category.name}
                      {selectedCategories.includes(category.name) && ' ✓'}
                    </Badge>
                  ))}
                  {selectedCategories.length > 0 && (
                    <button
                      onClick={() => setSelectedCategories([])}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2 text-gray-600">
                <Filter className="w-5 h-5" />
                <span className="font-medium text-sm sm:text-base">Filters:</span>
              </div>
              
              <Select value={mode || 'all'} onValueChange={(v) => handleFilterChange('mode', v)}>
                <SelectTrigger className="w-32 sm:w-40">
                  <SelectValue placeholder="Course Mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modes</SelectItem>
                  <SelectItem value="live_web">Live Web</SelectItem>
                  <SelectItem value="self_paced">Self-Paced</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={difficulty || 'all'} onValueChange={(v) => handleFilterChange('difficulty', v)}>
                <SelectTrigger className="w-32 sm:w-40">
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="ml-auto text-sm text-gray-500">
                {pagination.total_items} courses found
              </div>
            </div>
          </div>
          
          {/* Course Grid */}
          {courses.length === 0 ? (
            <Card className="text-center py-12 sm:py-16">
              <CardContent>
                <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No courses found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {courses.map((course) => (
                <Card 
                  key={course.id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group border-0 shadow-md"
                  onClick={() => navigate(`/courses/${course.slug || course.id}`)}
                >
                  {/* Thumbnail */}
                  <div className="relative h-40 sm:h-48 bg-gradient-to-br from-indigo-400 to-purple-500">
                    {course.thumbnail_url ? (
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <GraduationCap className="w-16 sm:w-20 h-16 sm:h-20 text-white/50" />
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className={`${getDifficultyColor(course.difficulty_level)} capitalize text-xs`}>
                        {course.difficulty_level}
                      </Badge>
                      {course.is_free && (
                        <Badge className="bg-green-500 text-white text-xs">Free</Badge>
                      )}
                    </div>
                    
                    {/* Mode Badge */}
                    <div className="absolute top-3 right-3">
                      <Badge variant="secondary" className="bg-white/90 text-gray-700 capitalize text-xs">
                        {getModeIcon(course.mode)}
                        <span className="ml-1">{getModeLabel(course.mode)}</span>
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {course.title}
                    </h3>
                    {course.short_description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {stripHtmlTags(course.short_description)}
                      </p>
                    )}
                    
                    {/* Instructor - Show new instructor_name field or primary_instructor */}
                    {(course.instructor_name || course.primary_instructor) && (
                      <div className="flex items-center gap-2 mb-3">
                        {course.instructor_image ? (
                          <img 
                            src={course.instructor_image} 
                            alt="" 
                            className="w-7 h-7 sm:w-8 sm:h-8 object-cover border border-gray-200" loading="lazy" decoding="async"/>
                        ) : course.primary_instructor?.avatar_url ? (
                          <img 
                            src={course.primary_instructor.avatar_url} 
                            alt="" 
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" loading="lazy" decoding="async"/>
                        ) : (
                          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-medium text-xs sm:text-sm">
                              {(course.instructor_name || course.primary_instructor?.full_name)?.charAt(0) || 'I'}
                            </span>
                          </div>
                        )}
                        <span className="text-sm text-gray-600 truncate">
                          {course.instructor_name || course.primary_instructor?.full_name}
                        </span>
                      </div>
                    )}
                    
                    {/* Stats - only show if values exist */}
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      {course.duration_weeks > 0 && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{course.duration_weeks}w</span>
                        </div>
                      )}
                      {(course.total_enrolled > 0) && (
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{course.total_enrolled}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  
                  <CardFooter className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                    {!isMobileApp() && formatPrice(course.price_inr, course.discount_price_inr)}
                    <Button 
                      size="sm" 
                      className={`bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm ${isMobileApp() ? 'w-full' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/courses/${course.slug || course.id}`);
                      }}
                    >
                      View
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {[...Array(pagination.total_pages)].map((_, i) => (
                <Button
                  key={i}
                  variant={pagination.page === i + 1 ? "default" : "outline"}
                  size="sm"
                  onClick={() => fetchCourses(i + 1)}
                  className={pagination.page === i + 1 ? "bg-indigo-600" : ""}
                >
                  {i + 1}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CourseCatalog;
